import { useEffect, useReducer } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Icon,
  Spinner,
  Button,
  Link,
} from '@chakra-ui/react';
import { XIcon } from '@heroicons/react/outline';
import { BluprintTypeFilter } from './components/filters/blueprint-type';
import { BlueprintFunctions } from './components/filters/functions';
import { Search } from './components/filters/search.jsx';
import { BlueprintList } from './components/blueprint-list';
import { BlueprintDetails } from './components/blueprint-details';
import { EmptyBlueprint } from './components/EmptyBlueprint';
import { useGetAllBluePrintQuery } from '@/store/query/blueprint';
import { blueprintReducer, defaultState } from './state';
import { KText } from '@/ui-library/components/text';
import { AtsDeptFunction } from './components/filters/AtsDeptFunction';
import { useKFlags } from '@/hooks/use-k-flags';

export const BlueprintListWithDetails = ({
  onStartFromScratch = () => {},
  onCreateFromBlueprint = () => {},
  onClose = () => {},
}) => {
  const { data: blueprintsData, isLoading } = useGetAllBluePrintQuery();
  const [state, dispatch] = useReducer(blueprintReducer, defaultState);
  const { blueprints, selectedBlueprint } = state;
  const { ats_enabled } = useKFlags();

  const onSelectBlueprint = (blueprint) => {
    dispatch({
      type: 'set-selected-blueprint',
      payload: { blueprint },
    });
  };

  const onClearFilter = () => {
    dispatch({
      type: 'clear-filter',
    });
  };

  const onSelectType = (item) => {
    dispatch({
      type: 'filter',
      payload: {
        type: 'type',
        value: item?.value,
      },
    });
  };
  const onSelectFunctions = (item) => {
    dispatch({
      type: 'filter',
      payload: {
        type: 'function',
        value: item?.value,
        ats_enabled,
      },
    });
  };
  const onSearch = (searchTerm) => {
    dispatch({
      type: 'filter',
      payload: {
        type: 'search',
        value: searchTerm,
      },
    });
  };

  useEffect(() => {
    if (isLoading || !blueprintsData?.length) return;
    dispatch({ type: 'init', payload: { blueprints: blueprintsData } });
  }, [isLoading, blueprintsData]);

  return (
    <Box height="inherit">
      <Flex
        height="58px"
        px="22px"
        alignItems="center"
        borderBottom="1px solid #E5E5E5"
      >
        <KText flex="1" variant="heading_1">
          Create Kula Flows
        </KText>
        <IconButton
          variant="icon"
          ml="16px"
          onClick={onClose}
          icon={
            <Icon as={XIcon} width="16px" height="16px" color="secondary-700" />
          }
        />
      </Flex>
      <Flex
        height="65px"
        borderBottom="1px solid #E5E5E5"
        px="22px"
        alignItems="center"
      >
        <Flex flex={1} alignItems="center">
          <Box>
            <BluprintTypeFilter onSelect={onSelectType} filter={state} />
          </Box>
          <Box ml="12px">
            {ats_enabled ? (
              <AtsDeptFunction onSelect={onSelectFunctions} filter={state} />
            ) : (
              <BlueprintFunctions onSelect={onSelectFunctions} filter={state} />
            )}
          </Box>
          {(state?.type || state?.function) && (
            <Box ml="12px">
              <Link
                color="secondary-700"
                fontSize="14px"
                fontWeight="medium"
                textDecor="underline"
                onClick={onClearFilter}
              >
                Clear Filters
              </Link>
            </Box>
          )}
        </Flex>
        <Box>
          <Button
            width="full"
            onClick={onStartFromScratch}
            id="ob-start-from-scratch"
          >
            + Start from scratch
          </Button>
        </Box>
      </Flex>
      {isLoading ? (
        <Flex
          height="calc(100% - 123px)"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner color="#0043CE" />
        </Flex>
      ) : (
        <Flex height="calc(100% - 123px)" flexDirection={'row'}>
          <Box
            width="300px"
            borderRight="1px solid"
            borderColor="border-color"
            pb="22px"
          >
            <Search onSearch={onSearch} />
            {blueprints?.length ? (
              <BlueprintList
                blueprints={blueprints}
                selected={selectedBlueprint}
                onSelect={onSelectBlueprint}
              />
            ) : (
              <EmptyBlueprint onStartFromScratch={onStartFromScratch} />
            )}
          </Box>
          <Box flex="1">
            {selectedBlueprint && (
              <BlueprintDetails
                blueprint={selectedBlueprint}
                onCreateFromBlueprint={onCreateFromBlueprint}
              />
            )}
          </Box>
        </Flex>
      )}
    </Box>
  );
};
